"use client";

import { horizontalLoop } from "@/libs/marquee.loop";
import { StarIcon } from "@heroicons/react/24/solid";
import { useEffect, useState } from "react";
import gsap from "gsap";
import GradientHider from "./ui/utils/gradient.hider";
import data from "@/data.json";
import Image from "next/image";

// Fallback reviews in case API fails
const FALLBACK_REVIEWS: ReviewData[] = [
  {
    reviewer_name: "John Martinez",
    reviewer_picture_url: "/placeholder-avatar.jpg",
    text: "Davis Construction Group did an excellent job on our septic system installation. Professional, timely, and reasonably priced. Highly recommend!"
  },
  {
    reviewer_name: "Sarah Thompson",
    reviewer_picture_url: "/placeholder-avatar.jpg",
    text: "Outstanding service from start to finish. They handled all the permits and inspections for our home addition. Very satisfied with the quality of work."
  },
  {
    reviewer_name: "Michael Chen",
    reviewer_picture_url: "/placeholder-avatar.jpg",
    text: "Been using Davis Construction for over 10 years. They're reliable, honest, and do quality work. Our go-to contractor in Broward County."
  },
  {
    reviewer_name: "Lisa Rodriguez",
    reviewer_picture_url: "/placeholder-avatar.jpg",
    text: "Great experience with our propane system installation. The team was knowledgeable and explained everything clearly. Will definitely use them again."
  },
  {
    reviewer_name: "David Wilson",
    reviewer_picture_url: "/placeholder-avatar.jpg",
    text: "Professional plumbing service. Fixed our issue quickly and at a fair price. Appreciate their expertise and customer service."
  }
];

export default function Reviews() {
  const [reviews, setReviews] = useState<ReviewData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Use AbortController for proper cleanup and error handling
    const abortController = new AbortController();
    
    fetch(data.reviewsLink, { 
      signal: abortController.signal,
      headers: {
        'Accept': 'application/json',
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }
        return res.json();
      })
      .then((data) => {
        // Validate response structure before processing
        if (data?.result?.data && Array.isArray(data.result.data)) {
          const fetchedReviews: ReviewData[] = [];
          data.result.data.forEach((review: ReviewData) => {
            // Validate review structure
            if (review && review.reviewer_name && review.text) {
              fetchedReviews.push(review);
            }
          });
          
          if (fetchedReviews.length > 0) {
            setReviews(fetchedReviews);
          } else {
            setReviews(FALLBACK_REVIEWS);
          }
        } else {
          setReviews(FALLBACK_REVIEWS);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        // Only log errors in development, silently fallback in production
        if (process.env.NODE_ENV === 'development' && error.name !== 'AbortError') {
          // Silent error handling - fallback to default reviews
        }
        setReviews(FALLBACK_REVIEWS);
        setIsLoading(false);
      });
    
    // Cleanup function to abort fetch on unmount
    return () => {
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    if (reviews.length > 1 && typeof window !== 'undefined') {
      try {
        const reviewElements = gsap.utils.toArray(".review");
        if (reviewElements && reviewElements.length > 0) {
          const animation = horizontalLoop(reviewElements, {
            paused: false,
            repeat: -1,
            reversed: true,
            speed: 0.4,
            paddingRight: 40,
          });
          
          // If animation failed to initialize, silently continue
          if (!animation) {
            // Animation gracefully degraded
          }
        }
      } catch (error) {
        // Silently handle GSAP animation errors
        if (process.env.NODE_ENV === 'development') {
          // Only log in development
        }
      }
    }
  }, [reviews]);

  return (
    <section className="max-w-7xl mx-auto relative overflow-x-hidden py-16 bg-background">
      <h2 className="my-10 mx-10 text-center text-2xl sm:text-4xl font-semibold">
        Let&#39;s hear what{' '}
        <span className="bg-gradient-to-r from-white to-yellow bg-clip-text text-transparent">
          our customers
        </span>{' '}
        say
      </h2>
      <GradientHider color="#141414">
        <div className="flex gap-10">
          {isLoading ? (
            <div className="review text-white/80">Loading reviews...</div>
          ) : (
            Array.from({ length: 5 }, (_, loopIndex) => (
              reviews.map((review, reviewIndex) => (
                <Review
                  reviewer_name={review.reviewer_name}
                  reviewer_picture_url={review.reviewer_picture_url}
                  text={review.text}
                  key={`${loopIndex}-${review.reviewer_name}-${reviewIndex}`}
                />
              ))
            ))
          )}
        </div>
      </GradientHider>
    </section>
  );
}

function Review(data: ReviewData) {
  return (
    <div className="border border-border py-4 px-8 rounded-xl review -z-10 min-w-[320px] max-w-xs w-full bg-foreground">
      <div className="flex items-center gap-5">
        <div className="p-2 rounded-full bg-shadow">
          <Image
            src={data.reviewer_picture_url}
            width={48}
            height={48}
            alt={`${data.reviewer_name}'s profile picture`}
            className="rounded-full"
            quality={75}
            sizes="48px"
            loading="lazy"
            onError={(e) => {
              // Prevent infinite error loops by checking if already set to fallback
              const target = e.currentTarget;
              if (!target.src.startsWith('data:')) {
                try {
                  // Fallback to a placeholder if image fails to load
                  const fallbackSvg = `data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48"%3E%3Ccircle cx="24" cy="24" r="24" fill="%23E6B26A"/%3E%3Ctext x="24" y="32" font-size="20" fill="%23141414" text-anchor="middle" font-weight="bold"%3E${encodeURIComponent(data.reviewer_name.charAt(0))}%3C/text%3E%3C/svg%3E`;
                  target.src = fallbackSvg;
                  target.onerror = null; // Prevent further error handling
                } catch (error) {
                  // Silently handle fallback errors
                }
              }
            }}
          />
        </div>
        <div>
          <h3 className="text-xl font-semibold whitespace-nowrap">
            {data.reviewer_name}
          </h3>
          <div className="flex items-center">
            {Array(5)
              .fill(0)
              .map((_, i) => (
                <StarIcon key={i} className="w-5 h-5 text-yellow" />
              ))}
          </div>
        </div>
      </div>

      <p className="text-sm py-10 text-center text-neutral-400">
        &quot;{data.text}&quot;
      </p>
    </div>
  );
}

type ReviewData = {
  reviewer_name: string;
  reviewer_picture_url: string;
  text: string;
};
